import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtINR, fmtDate, BUCKET_STYLE } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { CheckCircle2, AlertTriangle, Calendar, Sparkles, MessageCircle, Search, ChevronDown, X as XIcon, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import NewFollowupDialog from "@/components/NewFollowupDialog";
import WhatsAppButton from "@/components/WhatsAppButton";
import CallButton from "@/components/CallButton";
import EmailButton from "@/components/EmailButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import EmployeeFilter from "@/components/EmployeeFilter";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";

// Lightweight title→category mapping. We keep this in the FE so we don't
// have to migrate existing follow-up rows; over time, if the team relies
// on these chips heavily, we can promote it to a stored field.
const FU_CATEGORIES = [
  { key: "quote",     label: "Quote f/u",      hits: ["quote", "quotation"], tone: "bg-primary-50 text-primary-700 border-primary-200" },
  { key: "purchase",  label: "Purchase enq",   hits: ["purchase", "po ", "po:", "po/", "enquiry"], tone: "bg-sky-50 text-sky-800 border-sky-200" },
  { key: "demo",      label: "Demo",           hits: ["demo", "demonstration", "site visit"], tone: "bg-amber-50 text-amber-800 border-amber-200" },
  { key: "negotiation", label: "Negotiation",  hits: ["negotiat"], tone: "bg-purple-50 text-purple-800 border-purple-200" },
  { key: "general",   label: "General",        hits: [], tone: "bg-muted/60 text-muted-foreground border-border/60" },
];

function categorizeFollowup(fu) {
  const t = `${fu.title || ""} ${fu.deal_title || ""}`.toLowerCase();
  for (const cat of FU_CATEGORIES) {
    if (cat.key === "general") continue;
    if (cat.hits.some((h) => t.includes(h))) return cat.key;
  }
  return "general";
}

function FollowupCategoryChips({ items, active, onPick }) {
  const counts = items.reduce((acc, fu) => {
    const k = categorizeFollowup(fu);
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const visible = FU_CATEGORIES.filter((c) => (counts[c.key] || 0) > 0);
  if (visible.length <= 1) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-3 sm:mb-4" data-testid="fu-category-chips">
      {visible.map((c) => {
        const isActive = active === c.key;
        return (
          <button
            key={c.key}
            onClick={() => onPick(c.key)}
            data-testid={`fu-chip-${c.key}`}
            className={`px-3 h-9 rounded-2xl border text-xs font-bold tap transition inline-flex items-center gap-1.5 ${
              isActive ? "bg-primary text-primary-foreground border-primary" : c.tone
            }`}
          >
            {c.label}
            <span className={`text-[11px] px-1.5 rounded-full tabular-nums ${isActive ? "bg-white/20" : "bg-white/60"}`}>
              {counts[c.key] || 0}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function FollowUps() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialBucket = params.get("bucket") || "today";
  const [scope, setScope] = useState("mine"); // mine|team|all
  const [data, setData] = useState({ overdue: [], today: [], upcoming: [] });
  const [missing, setMissing] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(initialBucket);
  const [newDialog, setNewDialog] = useState(params.get("new") === "1");
  const [q, setQ] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const canTeam = user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/followups", { params: { scope, owner_id: ownerId || undefined } });
      setData(data);
      const m = await api.get("/followups", { params: { scope, bucket: "missing", owner_id: ownerId || undefined } });
      setMissing(m.data.missing || []);
      const c = await api.get("/followups", { params: { scope, bucket: "completed", owner_id: ownerId || undefined, days: 30 } });
      setCompleted(c.data.completed || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, ownerId]);
  const { indicator: pullIndicator } = usePullToRefresh(load);

  const complete = async (id) => {
    try {
      await api.post(`/followups/${id}/complete`);
      toast.success("Marked done");
      load();
    } catch { toast.error("Failed"); }
  };

  const skip = async (id) => {
    const reason = window.prompt("Why skip this follow-up? (optional)") || "";
    try {
      await api.post(`/followups/${id}/skip`, { reason: reason.trim() || null });
      toast.success("Marked not done");
      load();
    } catch { toast.error("Failed"); }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-12 w-48" />
        <Skeleton className="h-96 rounded-3xl" />
      </div>
    );
  }

  const renderRow = (fu) => {
    const pingedAgo = fu.client_pinged_at ? (() => {
      const ms = Date.now() - new Date(fu.client_pinged_at).getTime();
      const min = Math.round(ms / 60000);
      if (min < 1) return "just now";
      if (min < 60) return `${min}m ago`;
      const h = Math.round(min / 60);
      if (h < 24) return `${h}h ago`;
      return `${Math.round(h / 24)}d ago`;
    })() : null;
    return (
      <div
        key={fu.id}
        data-testid={`fu-${fu.id}`}
        className="flex items-start gap-3 p-3.5 rounded-2xl border border-border/60 bg-card hover:bg-muted/30 transition"
      >
        {/* Done / Not done dropdown — green tick fires Done; chevron opens menu. */}
        <div className="flex shrink-0 mt-0.5 rounded-full overflow-hidden border-2 border-border/80 hover:border-primary group">
          <button
            data-testid={`fu-complete-${fu.id}`}
            onClick={() => complete(fu.id)}
            className="h-9 w-9 grid place-items-center group-hover:bg-primary-50 tap"
            aria-label="Mark done"
            title="Mark done"
          >
            <CheckCircle2 className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={`fu-action-menu-${fu.id}`}
                className="h-9 w-5 grid place-items-center border-l border-border/60 group-hover:bg-primary-50 tap"
                aria-label="More options"
              >
                <ChevronDown className="h-3 w-3 text-muted-foreground group-hover:text-primary" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="rounded-2xl">
              <DropdownMenuItem onClick={() => complete(fu.id)} data-testid={`fu-mark-done-${fu.id}`}>
                <CheckCircle2 className="h-4 w-4 mr-2 text-primary" /> Mark as done
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => skip(fu.id)} data-testid={`fu-mark-skip-${fu.id}`} className="text-amber-700 focus:text-amber-800">
                <XIcon className="h-4 w-4 mr-2" /> Mark as not done
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex-1 min-w-0 space-y-1.5">
          {/* Tap title block to open the deal page when present — gives reps
              a one-tap path to full context without needing a popup. */}
          <div
            role={fu.deal_id ? "button" : undefined}
            tabIndex={fu.deal_id ? 0 : undefined}
            onClick={() => fu.deal_id && navigate(`/deals/${fu.deal_id}`)}
            className={`flex items-start justify-between gap-2 ${fu.deal_id ? "cursor-pointer" : ""}`}
          >
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm leading-snug break-words" data-testid={`fu-title-${fu.id}`}>{fu.title}</p>
              <p className="text-xs text-muted-foreground break-words mt-0.5">
                {fu.client_contact_person ? `${fu.client_contact_person} · ` : ""}
                {fu.client_name || "—"}{fu.deal_title ? ` · ${fu.deal_title}` : ""}
                {fu.deal_value ? ` · ${fmtINR(fu.deal_value)}` : ""}
              </p>
              {fu.deal_stage && (
                <Badge variant="outline" className="mt-1 text-[9px] uppercase tracking-wider px-1.5 py-0 capitalize" data-testid={`fu-stage-${fu.id}`}>
                  {fu.deal_stage.replaceAll("_", " ")}
                </Badge>
              )}
            </div>
            <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap shrink-0">{fmtDate(fu.due_date)}</span>
          </div>
          {/* Direct contact details — phone & email visible inline so reps can
              read the number without opening a separate dialog. */}
          {(fu.client_phone || fu.client_email) && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
              {fu.client_phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{fu.client_phone}</span>}
              {fu.client_email && <span className="inline-flex items-center gap-1 break-all"><Mail className="h-3 w-3" />{fu.client_email}</span>}
            </div>
          )}
          {/* Notes / context if captured at follow-up creation time. */}
          {(fu.notes || fu.context) && (
            <p className="text-[11px] italic text-muted-foreground bg-muted/30 rounded-lg px-2.5 py-1.5 break-words" data-testid={`fu-notes-${fu.id}`}>
              "{fu.notes || fu.context}"
            </p>
          )}
          {(pingedAgo || fu.client_phone || fu.client_email) && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-2 pt-0.5">
              {pingedAgo ? (
                <Badge
                  variant="outline"
                  className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] uppercase tracking-wider inline-flex items-center gap-1 self-start shrink-0"
                  data-testid={`fu-pinged-${fu.id}`}
                >
                  <MessageCircle className="h-2.5 w-2.5" />
                  Pinged · {pingedAgo}
                </Badge>
              ) : <span className="hidden sm:inline" />}
              <div className="flex items-center gap-1.5 shrink-0 sm:ml-auto flex-wrap">
                {fu.client_phone && <CallButton phone={fu.client_phone} testId={`call-fu-${fu.id}`} />}
                {fu.client_phone && (
                  <WhatsAppButton
                    phone={fu.client_phone}
                    email={fu.client_email}
                    contactName={fu.client_contact_person || fu.client_name}
                    dealTitle={fu.deal_title}
                    dealId={fu.deal_id}
                    clientId={fu.client_id}
                    clientCompany={fu.client_name}
                    action={fu.title}
                    defaultTemplate="follow_up_action"
                    followupId={fu.id}
                    label="WhatsApp"
                    onSent={load}
                    testId={`wa-fu-${fu.id}`}
                  />
                )}
                {fu.client_email && (
                  <EmailButton
                    email={fu.client_email}
                    phone={fu.client_phone}
                    contactName={fu.client_contact_person || fu.client_name}
                    dealTitle={fu.deal_title}
                    dealId={fu.deal_id}
                    clientId={fu.client_id}
                    clientCompany={fu.client_name}
                    action={fu.title}
                    defaultTemplate="follow_up_action"
                    followupId={fu.id}
                    onSent={load}
                    testId={`email-fu-${fu.id}`}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-8 lg:p-10 max-w-[1100px] mx-auto">
      {pullIndicator}
      <header className="mb-3 sm:mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Follow-ups</p>
          <h1 className="font-display text-2xl sm:text-4xl font-black tracking-tight mt-0.5 sm:mt-1">Discipline dashboard.</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canTeam && (
            <Tabs value={scope} onValueChange={setScope}>
              <TabsList className="rounded-2xl">
                <TabsTrigger value="mine" data-testid="scope-mine" className="rounded-xl">Mine</TabsTrigger>
                <TabsTrigger value="all" data-testid="scope-all" className="rounded-xl">All</TabsTrigger>
              </TabsList>
            </Tabs>
          )}
          <EmployeeFilter value={ownerId} onChange={setOwnerId} testId="followups-employee-filter" />
          <Button onClick={() => setNewDialog(true)} className="rounded-2xl tap" data-testid="new-followup-button">
            New follow-up
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
        {["overdue", "today", "upcoming"].map((k) => {
          const s = BUCKET_STYLE[k];
          return (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`fu-tab-${k}`}
              className={`p-2.5 sm:p-5 rounded-2xl sm:rounded-3xl border text-left tap card-lift overflow-hidden ${
                tab === k ? "bg-card border-primary shadow-soft" : "bg-muted/30 border-border/60"
              }`}
            >
              <p className={`text-[9px] sm:text-[10px] uppercase tracking-[0.06em] sm:tracking-[0.18em] font-bold flex items-center gap-1 sm:gap-1.5 whitespace-nowrap ${
                k === "overdue" ? "text-red-600" : k === "today" ? "text-amber-600" : "text-emerald-600"
              }`}>
                <span className={`h-1.5 w-1.5 rounded-full ${s.dot} shrink-0`} /> {s.label}
              </p>
              <p className={`mt-1 sm:mt-3 font-display text-xl sm:text-4xl font-black leading-none ${
                k === "overdue" ? "text-red-600" : k === "today" ? "text-amber-600" : "text-emerald-600"
              }`}>{(data[k] || []).length}</p>
            </button>
          );
        })}
      </div>

      {/* Follow-up category chips — derived from the title of each open
          follow-up. Quick visual cue of where the team's energy is going.
          Click a chip to filter; click again to clear. */}
      <FollowupCategoryChips
        items={[...(data.overdue || []), ...(data.today || []), ...(data.upcoming || [])]}
        active={categoryFilter}
        onPick={(c) => setCategoryFilter(categoryFilter === c ? "" : c)}
      />

      <div className="mb-3 sm:mb-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by client, deal or action…"
            className="pl-10 h-11 rounded-2xl bg-muted/40"
            data-testid="followup-search-input"
          />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-2xl mb-4">
          <TabsTrigger value="overdue" className="rounded-xl">Overdue</TabsTrigger>
          <TabsTrigger value="today" className="rounded-xl">Due today</TabsTrigger>
          <TabsTrigger value="upcoming" className="rounded-xl">Upcoming</TabsTrigger>
          <TabsTrigger value="missing" className="rounded-xl" data-testid="fu-tab-missing">Missing</TabsTrigger>
          <TabsTrigger value="completed" className="rounded-xl" data-testid="fu-tab-completed">Completed</TabsTrigger>
        </TabsList>
        {["overdue", "today", "upcoming"].map((k) => {
          const needle = q.trim().toLowerCase();
          const list = (data[k] || []).filter((fu) => {
            if (categoryFilter && categorizeFollowup(fu) !== categoryFilter) return false;
            if (!needle) return true;
            return [fu.title, fu.client_name, fu.deal_title, fu.client_email].some(
              (v) => (v || "").toLowerCase().includes(needle)
            );
          });
          return (
          <TabsContent key={k} value={k}>
            <Card className="rounded-3xl border-border/60 shadow-soft">
              <CardContent className="p-4 space-y-2">
                {list.length === 0 ? (
                  <div className="py-12 text-center">
                    <Sparkles className="h-6 w-6 text-primary mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">{needle ? "No follow-ups match your search." : "All clear in this bucket."}</p>
                  </div>
                ) : (
                  list.map(renderRow)
                )}
              </CardContent>
            </Card>
          </TabsContent>
          );
        })}
        <TabsContent value="missing">
          <Card className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-4 space-y-2">
              <p className="text-xs text-muted-foreground mb-3">
                Active deals (not won/lost) without any pending follow-up. Add an action to keep them moving.
              </p>
              {(() => {
                const needle = q.trim().toLowerCase();
                const list = needle
                  ? missing.filter((d) => [d.title, d.stage].some((v) => (v || "").toLowerCase().includes(needle)))
                  : missing;
                if (list.length === 0) {
                  return <p className="py-8 text-center text-sm text-muted-foreground">{needle ? "No deals match your search." : "Every active deal has a follow-up. Nice."}</p>;
                }
                return list.map((d) => (
                  <div
                    key={d.id}
                    onClick={() => navigate(`/deals/${d.id}`)}
                    className="flex items-center gap-3 p-3.5 rounded-2xl border border-border/60 bg-card hover:bg-muted/30 transition cursor-pointer tap"
                    data-testid={`missing-deal-${d.id}`}
                  >
                    <span className="h-9 w-9 rounded-full bg-amber-50 text-amber-700 grid place-items-center shrink-0">
                      <AlertTriangle className="h-4 w-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{d.title}</p>
                      <p className="text-xs text-muted-foreground truncate capitalize">
                        {(d.stage || "").replace(/_/g, " ")} · {fmtINR(d.value)}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-800 border-amber-200">
                      No follow-up
                    </Badge>
                  </div>
                ));
              })()}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Closed/skipped follow-ups — read-only history (last 30 days). */}
      <Tabs value={tab}>
        <TabsContent value="completed">
          <Card className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-4 space-y-2">
              <p className="text-xs text-muted-foreground mb-3">
                Last 30 days of completed and skipped follow-ups.
              </p>
              {(() => {
                const needle = q.trim().toLowerCase();
                const list = needle
                  ? completed.filter((fu) => [fu.title, fu.client_name, fu.deal_title, fu.client_email].some((v) => (v || "").toLowerCase().includes(needle)))
                  : completed;
                if (list.length === 0) {
                  return <p className="py-8 text-center text-sm text-muted-foreground">{needle ? "No completed follow-ups match." : "No follow-ups closed in the last 30 days."}</p>;
                }
                return list.map((fu) => (
                  <div
                    key={fu.id}
                    onClick={() => fu.deal_id && navigate(`/deals/${fu.deal_id}`)}
                    className={`flex items-start gap-3 p-3.5 rounded-2xl border border-border/60 bg-card hover:bg-muted/30 transition ${fu.deal_id ? "cursor-pointer" : ""}`}
                    data-testid={`fu-completed-${fu.id}`}
                  >
                    <span className={`h-9 w-9 rounded-full grid place-items-center shrink-0 ${fu.status === "completed" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                      {fu.status === "completed" ? <CheckCircle2 className="h-4 w-4" /> : <XIcon className="h-4 w-4" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{fu.title}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {fu.client_name || "—"}{fu.deal_title ? ` · ${fu.deal_title}` : ""}
                      </p>
                      {fu.skip_reason && <p className="text-[11px] italic text-amber-700 mt-1">Reason: {fu.skip_reason}</p>}
                    </div>
                    <div className="text-right shrink-0">
                      <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${fu.status === "completed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                        {fu.status === "completed" ? "Done" : "Skipped"}
                      </Badge>
                      {fu.completed_at && (
                        <p className="text-[10px] text-muted-foreground mt-1 whitespace-nowrap">{fmtDate(fu.completed_at)}</p>
                      )}
                    </div>
                  </div>
                ));
              })()}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <NewFollowupDialog open={newDialog} onOpenChange={setNewDialog} onCreated={load} />
    </div>
  );
}
